import type { ClubTheme } from './themes';
import type { SocialAuthor } from './types';

export interface QuotedPostData {
  id: string;
  body: string | null;
  author: SocialAuthor | null;
  created_at: string;
}

interface QuotedPostEmbedProps {
  post: QuotedPostData;
  theme: ClubTheme;
  onNavigate?: (postId: string) => void;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString();
}

export default function QuotedPostEmbed({ post, theme, onNavigate }: QuotedPostEmbedProps) {
  const authorName = post.author?.full_name || 'Unknown';
  const snippet = post.body
    ? post.body.length > 200
      ? post.body.slice(0, 200) + '...'
      : post.body
    : '';

  return (
    <div
      className={`mt-3 rounded-xl p-3 transition-all ${onNavigate ? 'cursor-pointer hover:opacity-80 hover:shadow-sm' : ''}`}
      style={{
        backgroundColor: `${theme.colors.muted}08`,
        borderLeft: `3px solid ${theme.colors.primary}`,
        border: `1px solid ${theme.colors.muted}15`,
        borderLeftWidth: '3px',
        borderLeftColor: theme.colors.primary,
      }}
      onClick={() => {
        if (onNavigate) {
          onNavigate(post.id);
        } else {
          // Fallback: try to scroll to the post in the feed
          const el = document.getElementById(`post-${post.id}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.outline = `2px solid ${theme.colors.primary}`;
            el.style.borderRadius = '16px';
            setTimeout(() => { el.style.outline = ''; }, 2000);
          }
        }
      }}
    >
      <div className="flex items-center gap-2">
        {post.author?.avatar_url ? (
          <img src={post.author.avatar_url} alt="" className="h-5 w-5 rounded-full object-cover" />
        ) : (
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold"
            style={{ backgroundColor: `${theme.colors.primary}30`, color: theme.colors.primary }}
          >
            {(authorName[0] || '?').toUpperCase()}
          </div>
        )}
        <span className="text-xs font-semibold" style={{ color: theme.colors.text }}>
          {authorName}
        </span>
        <span className="text-[10px]" style={{ color: theme.colors.muted }}>
          {timeAgo(post.created_at)}
        </span>
      </div>

      {snippet && (
        <p
          className="mt-1.5 text-xs leading-relaxed line-clamp-3"
          style={{ color: `${theme.colors.text}CC` }}
        >
          {snippet}
        </p>
      )}
    </div>
  );
}
